import { z } from 'zod';
import { isBefore, startOfDay, endOfDay } from 'date-fns';

import { getStartOfDayInTimezone, parseUTCDate } from 'utils/dateTimeUtils';
import { EditableSession, CapacityType, EventFormat } from 'types/event.types';
import { CapacityModalSchema } from 'pages/dashboard/overview/events/schemas';
import { targetAttendeesModalSchema } from 'pages/dashboard/overview/events/components';
import {
  validateEventDescriptionNotEmpty,
  getEventFormatFromApi,
  convertSendAtToFormValue,
  convertSendAtToDBValue
} from 'pages/dashboard/overview/events/schemas/commonUtils';
import { UpdateSessionPayload, PublishEventSessionInput } from '@event-editor/api/eventSessions';

const validateSessionTiming = (
  data: z.infer<typeof baseSchema>,
  ctx: z.RefinementCtx,
  timezoneCode: string,
  dbSession: EditableSession | null = null
): void => {
  // Check if session is published and datetime is unchanged
  const isPublishedWithUnchangedDateTime = (field: 'start' | 'end'): boolean => {
    if (!dbSession?.isPublished) return false;

    if (field === 'start') {
      const dbStartDateTime = dbSession.datetimeScheduled
        ? convertSendAtToFormValue(dbSession.datetimeScheduled, timezoneCode).getTime()
        : null;
      return dbStartDateTime === data.startDateTime.getTime();
    } else {
      const dbEndDateTime = dbSession.datetimeEnd
        ? convertSendAtToFormValue(dbSession.datetimeEnd, timezoneCode).getTime()
        : null;
      return dbEndDateTime === data.endDateTime.getTime();
    }
  };

  if (data.allDay) {
    // Skip validation if published and dates unchanged
    if (
      !isPublishedWithUnchangedDateTime('end') &&
      isBefore(endOfDay(data.endDateTime), startOfDay(data.startDateTime))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'End date must be same or after start date',
        path: ['endDateTime']
      });
    }
  } else {
    // Convert form dates to UTC for comparison using event's timezone
    const startDateTimeUTC = convertSendAtToDBValue(data.startDateTime, timezoneCode);
    const startDateTime = parseUTCDate(startDateTimeUTC);

    // For all-day events: compare with start of today in event's timezone
    // For regular events: compare with current time
    const timeToCompare = data.allDay ? getStartOfDayInTimezone(timezoneCode) : new Date();

    // Only show "start time in future" error if session is not published OR datetime changed
    if (!isPublishedWithUnchangedDateTime('start') && isBefore(startDateTime, timeToCompare)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Start time must be in the future',
        path: ['startDateTime.time']
      });
    }

    // Only show "end time after start" error if session is not published OR datetime changed
    if (
      !isPublishedWithUnchangedDateTime('end') &&
      isBefore(data.endDateTime, data.startDateTime)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'End time must be after start time',
        path: ['endDateTime.time']
      });
    }
  }
};

const plusOneConfigSchema = z
  .object({
    allowPlusOnes: z.boolean(),
    maxPlusOnes: z.coerce.number().int().min(1).max(10),
    collectQuestionnaireResponses: z.boolean()
  })
  .optional();

const baseSchema = z.object({
  title: z.string(),
  description: z.string(),
  startDateTime: z.date(),
  endDateTime: z.date(),
  allDay: z.boolean(),
  format: z.nativeEnum(EventFormat).optional(),
  virtualLink: z.string().nullish(),
  zoomMeetingId: z.number().optional(),
  googleMeetingId: z.number().optional(),
  inPersonLocation: z.string().nullish(),
  capacity: CapacityModalSchema,
  targetAttendance: targetAttendeesModalSchema.shape.targetAttendance,
  plusOneConfig: plusOneConfigSchema
});

const getCreateDraftSessionSchema = (timezoneCode: string) =>
  baseSchema.partial().superRefine((data, ctx) => {
    if (data.startDateTime && data.endDateTime) {
      validateSessionTiming(data as z.infer<typeof baseSchema>, ctx, timezoneCode);
    }
  });

type CreateDraftSessionFormValues = z.infer<ReturnType<typeof getCreateDraftSessionSchema>>;

const getUpdateSessionSchema = (session: EditableSession, timezoneCode: string) =>
  z
    .object({
      ...baseSchema.shape,
      title: baseSchema.shape.title.min(1, 'Title is required'),
      description: baseSchema.shape.description.refine(
        validateEventDescriptionNotEmpty,
        'Description is required'
      ),
      format: baseSchema.shape.format.refine((v) => !!v, 'Format is required')
    })
    .superRefine((data, ctx) => {
      if (session.hasEnded) {
        return;
      }

      validateSessionTiming(data as z.infer<typeof baseSchema>, ctx, timezoneCode, session);
    });

type UpdateSessionFormValues = z.infer<ReturnType<typeof getUpdateSessionSchema>>;

const sessionToUpdateFormValues = (
  session: EditableSession,
  timezoneCode: string
): UpdateSessionFormValues => {
  return {
    title: session.title ?? '',
    description: session.description ?? '',
    startDateTime: convertSendAtToFormValue(session.datetimeScheduled!, timezoneCode),
    endDateTime: convertSendAtToFormValue(session.datetimeEnd!, timezoneCode),
    allDay: session.allDay ?? false,
    format: getEventFormatFromApi(session) as EventFormat,
    virtualLink: session.virtualLocation,
    zoomMeetingId: session.zoomMeeting?.id,
    googleMeetingId: session.googleMeeting?.id,
    inPersonLocation: session.locationAddress,
    capacity: {
      maxAttendees: session.maxAttendees,
      capacityType: session.hasUnlimitedSpots ? CapacityType.UNLIMITED : CapacityType.LIMITED
    },
    targetAttendance: session.targetAttendance,
    plusOneConfig: session.plusOneConfig
      ? {
          allowPlusOnes: session.plusOneConfig.allowPlusOnes,
          maxPlusOnes: session.plusOneConfig.maxPlusOnes,
          collectQuestionnaireResponses: session.plusOneConfig.collectQuestionnaireResponses
        }
      : undefined
  };
};

const formValuesToPublishSessionApiPayload = (
  data: UpdateSessionFormValues,
  timezoneCode: string
): Omit<PublishEventSessionInput, 'eventId'> => ({
  // `eventId` and `sessionId` will be at API call/mutation level, not here
  title: data.title,
  description: data.description,
  startDatetime: convertSendAtToDBValue(data.startDateTime, timezoneCode),
  endDatetime: convertSendAtToDBValue(data.endDateTime, timezoneCode),
  allDay: data.allDay,
  isInPerson: data.format === EventFormat.IN_PERSON || data.format === EventFormat.HYBRID,
  isVirtual: data.format === EventFormat.VIRTUAL || data.format === EventFormat.HYBRID,
  virtualLink: data.virtualLink ?? undefined,
  zoomMeetingId: data.zoomMeetingId ?? undefined,
  googleMeetingId: data.googleMeetingId ?? undefined,
  inPersonLocation: data.inPersonLocation ?? undefined,
  maxAttendees: data.capacity?.maxAttendees ?? undefined,
  targetAttendance: data.targetAttendance ?? undefined,
  plusOneConfig: data.plusOneConfig
    ? {
        allowPlusOnes: data.plusOneConfig.allowPlusOnes,
        maxPlusOnes: data.plusOneConfig.maxPlusOnes,
        collectQuestionnaireResponses: data.plusOneConfig.collectQuestionnaireResponses
      }
    : undefined
});

const formValuesToUpdateSessionApiPayload = (
  data: UpdateSessionFormValues,
  session: EditableSession,
  timezoneCode: string
): UpdateSessionPayload => ({
  sessionId: session.id!,
  ...formValuesToPublishSessionApiPayload(data, timezoneCode)
});

export {
  getCreateDraftSessionSchema,
  getUpdateSessionSchema,
  sessionToUpdateFormValues,
  formValuesToUpdateSessionApiPayload,
  formValuesToPublishSessionApiPayload,
  type CreateDraftSessionFormValues,
  type UpdateSessionFormValues
};
